import {
	CategoryScale,
	Chart as ChartJS,
	LinearScale,
	LineElement,
	PointElement,
	Title,
	Tooltip,
} from 'chart.js'
import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import { AppContext } from '../../contexts/AppContext'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import SavingHeader from './components/SavingHeader'
import { useAchievements } from './hooks/useAchievements'
import EventsTab from './tabs/EventsTab'
import LevelTab from './tabs/LevelTab'
import ScoreboardTab from './tabs/ScoreboardTab'
import { SavingModeOptions } from './utils/styles/savingModeOptions'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip)

const Saving: FC<RouteComponentProps> = () => {
	const { isMobileView, isDarkMode } = useContext(AppContext)
	const achievementsHook = useAchievements()
	const { setPointsAwarded, setLastObservedStep, isLoading, currentMonthPoints } = achievementsHook

	const [selectedMode, setSelectedMode] = useState<SavingModeOptions>('level')
	const [displayedPoints, setDisplayedPoints] = useState(currentMonthPoints)
	const [shouldAnimate, setShouldAnimate] = useState(false)
	const pointsContainerRef = useRef<HTMLDivElement>(null)
	const { t } = useTranslation('pages_saving')

	useEffect(() => {
		setDisplayedPoints(currentMonthPoints)
		setShouldAnimate(false)
	}, [currentMonthPoints])

	const handlePointsAwarded = () => {
		setDisplayedPoints(currentMonthPoints)
		setShouldAnimate(true)
		setTimeout(() => setShouldAnimate(false), 1200)
	}

	const modeOptions: { mode: SavingModeOptions; label: string }[] = [
		{ mode: 'level', label: t('level') },
		{ mode: 'events', label: t('events') },
		{ mode: 'scoreboard', label: t('scoreboard') },
	]

	return (
		<PageWrapper
			page='saving'
			appBarOptions={{
				beta: true,
				overrideBackgroundStyle: 'bg-transparent dark:bg-transparent text-white !border-0',
			}}
		>
			<div
				className={`top-0 right-0 left-0 absolute w-full shadow-md clip-background z-10 ${isDarkMode ? 'saving-bg-dark' : 'saving-bg'} ${isMobileView ? 'aspect-square' : 'aspect-[1600/486]'}`}
			/>

			<div className='relative pb-5 mx-auto max-w-[1000px] z-20'>
				<SavingHeader
					displayedPoints={displayedPoints}
					shouldAnimate={shouldAnimate}
					pointsContainerRef={pointsContainerRef}
					selectedMode={selectedMode}
					onModeChange={setSelectedMode}
					modeOptions={modeOptions}
				/>

				<div
					className={`bg-[#f5f5f5] dark:bg-[#031f22] flex flex-col gap-3 rounded-t-2xl z-30 p-4 pb-0 ${isMobileView ? '' : 'rounded-b-2xl pb-4 mb-4'}`}
				>
					{selectedMode === 'level' && (
						<LevelTab
							achievements={achievementsHook}
							setPointsAwarded={setPointsAwarded}
							handlePointsAwarded={handlePointsAwarded}
							setLastObservedStep={setLastObservedStep}
							isLoading={isLoading}
							pointsContainerRef={pointsContainerRef}
						/>
					)}
					{selectedMode === 'events' && <EventsTab />}
					{selectedMode === 'scoreboard' && <ScoreboardTab achievementsHook={achievementsHook} />}
				</div>
			</div>
		</PageWrapper>
	)
}

export default Saving
